import { useCallback, useEffect, useRef, useState } from 'react'
import Fade from 'embla-carousel-fade'
import useEmblaCarousel from 'embla-carousel-react'
import { Lethargy } from 'lethargy-ts'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Media from '@/components/base/media'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'
import { WPImage, WPPageData } from '@/types'

type ConceptsPageProps = {
  pageProps: WPPageData & {
    collection?: {
      title: string
      link: string
      image: WPImage
      image_mobile: WPImage
    }[]
  }
}

const lethargy = new Lethargy()

const ConceptsPage = ({ pageProps }: ConceptsPageProps) => {
  const { strings } = useStore()
  const rootRef = useRef<HTMLDivElement>(null)
  const isAnimating = useRef(false)

  const items = pageProps.collection || []

  const [activeIndex, setActiveIndex] = useState(0)

  const [slideRef, slideApi] = useEmblaCarousel(
    {
      axis: 'y',
      loop: true,
      dragFree: false,
      duration: 50
    },
    [Fade()]
  )

  const [thickRef, thickApi] = useEmblaCarousel({
    axis: 'y',
    loop: true,
    dragFree: false,
    duration: 50,
    align: 'center'
  })

  useEffect(() => {
    if (!slideApi) return

    const onSelect = () => {
      const index = slideApi.selectedScrollSnap()
      setActiveIndex(index)
      thickApi?.scrollTo(index)
    }

    onSelect()
    slideApi.on('select', onSelect)
    slideApi.on('reInit', onSelect)

    return () => {
      slideApi.off('select', onSelect)
      slideApi.off('reInit', onSelect)
    }
  }, [slideApi, thickApi])

  useEffect(() => {
    const slideNode = rootRef.current
    if (!slideNode) return

    const handleWheel = (event: WheelEvent) => {
      if (lethargy.check(event) === false) return
      if (isAnimating.current) return

      event.preventDefault()

      isAnimating.current = true
      window.setTimeout(() => {
        isAnimating.current = false
      }, 500)

      if (event.deltaY < 0) {
        slideApi?.scrollPrev()
      } else {
        slideApi?.scrollNext()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        slideApi?.scrollPrev()
      } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        slideApi?.scrollNext()
      }
    }

    slideNode.addEventListener('wheel', handleWheel, { passive: false })
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      slideNode.removeEventListener('wheel', handleWheel)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [slideApi])

  const getWidthFromIndex = useCallback(
    (index: number) => {
      if (index === activeIndex) return { width: '100%', opacity: 1 }
      if (index === (activeIndex + items.length - 1) % items.length)
        return { width: '60%', opacity: 0.6 }
      if (index === (activeIndex + 1) % items.length)
        return { width: '60%', opacity: 0.6 }
      return { width: '30%', opacity: 0.3 }
    },
    [activeIndex, items.length]
  )

  return (
    <Section
      ref={rootRef}
      className={cn(
        'user-select-none fixed left-0 top-0 h-[calc(var(--vh)*100)] w-full overflow-hidden'
      )}
      data-layout="concepts-page"
    >
      <div ref={slideRef} className="size-full overflow-hidden">
        <div className="flex h-full flex-col">
          {items.map((item, index) => {
            const isActive = index === activeIndex

            return (
              <div
                key={index}
                className={cn(
                  'relative min-h-0 w-full flex-[0_0_100%] overflow-hidden',
                  isActive ? 'z-20' : 'z-10'
                )}
              >
                <div className="bg-black absolute inset-0">
                  <Media
                    media={{ poster: item.image, poster_mobile: item.image_mobile }}
                    unlockLoaderOnReady={index === 0}
                    className={cn(
                      'size-full object-cover opacity-80 transition-transform duration-1000',
                      'scale-[1.05]',
                      isActive && 'scale-100'
                    )}
                  />
                </div>

                <div className="absolute inset-0 z-10 flex items-end justify-center">
                  <Container
                    className={cn(
                      'text-white relative z-10 flex flex-col gap-spacing-lg pb-padding-md',
                      'transition-all duration-500',
                      isActive
                        ? 'translate-y-0 opacity-100 delay-200'
                        : 'translate-y-4 opacity-0'
                    )}
                  >
                    <Typography variant="h2" component="h2">{item.title}</Typography>

                    <CtaButton
                      variant="secondary"
                      href={item.link}
                      className="place-self-start"
                    >
                      {strings?.labels?.discover} {item.title}
                    </CtaButton>
                  </Container>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {items.length > 0 ? (
        <div
          className="absolute right-margin top-[calc(var(--vh)*50)] z-[10000] -translate-y-1/2"
          data-controller
        >
          <div
            ref={thickRef}
            className="w-[26px]"
            style={{
              height:
                16 *
                  Math.min(
                    items.length % 2 === 0 ? items.length - 1 : items.length,
                    5
                  ) -
                2
            }}
          >
            <div className="flex h-full flex-col">
              {items.map((_, index) => (
                <div
                  key={index}
                  className="relative flex w-full flex-[0_0_16px] items-center justify-end"
                >
                  <div
                    className="h-[2px] bg-[white] transition-all duration-700"
                    style={{
                      width: getWidthFromIndex(index).width,
                      opacity: getWidthFromIndex(index).opacity
                    }}
                  />

                  <span
                    className={cn(
                      'text-small absolute left-[-32px] opacity-0 transition-opacity duration-500',
                      {
                        'opacity-100 duration-700': index === activeIndex
                      }
                    )}
                  >
                    {(index + 1).toString().padStart(2, '0')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </Section>
  )
}

export default ConceptsPage
